<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="t('Conciliação de Despesas')"
    :steps="wizardStepsConfig"
    size="xlarge"
    :show-summary="currentStepIndex >= 2"
    :summary-items="summaryItems"
    :reset-on-close="true"
    @close="onClose"
    @cancel="onClose"
    @step-change="(handleStepChange as any)"
    @finish="completeExpenseConciliation">

    <!-- Step 0: Review/Add Expenses (NEW) -->
    <template #step-0="{ stepConfig }">
      <div class="step-panel">
        <h3>{{ t('Verificação de Despesas') }}</h3>
        <p class="step-description">
          {{ t('Verifique as despesas do período e adicione novas despesas antes de prosseguir com a conciliação.') }}
        </p>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <Button
            @click="openNewDespesaModal"
            severity="primary"
            :label="t('Nova despesa')"
            icon="pi pi-plus"
            data-testid="payroll-expense-new-despesa-button"
          />
          <Button
            @click="openCsvImportModal"
            severity="primary"
            :label="t('Importar de CSV')"
            icon="pi pi-upload"
            data-testid="payroll-expense-csv-import-button"
          />
          <Button
            @click="loadPeriodDespesas"
            :disabled="isLoadingPeriodDespesas"
            severity="secondary"
            size="small"
            :label="t('Atualizar')"
            :icon="isLoadingPeriodDespesas ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'"
            data-testid="payroll-expense-refresh-button"
          />
        </div>

        <!-- Period Despesas Summary -->
        <div v-if="periodDespesas.length > 0" class="selection-summary">
          <div class="summary-item">
            <span class="summary-label">{{ t('Despesas no Período') }}:</span>
            <span class="summary-value">{{ periodDespesas.length }}</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-label">{{ t('Total do Período') }}:</span>
            <span class="summary-value">{{ n(periodDespesasTotal, 'currency') }}</span>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoadingPeriodDespesas" class="loading-state">
          <i class="pi pi-spin pi-spinner"></i>
          <p>{{ t('Carregando despesas do período...') }}</p>
        </div>

        <!-- Period Despesas DataTable (view only) -->
        <DataTable
          v-else-if="periodDespesas.length > 0"
          :config="periodDespesasTableConfig"
          :data="periodDespesas"
          class="despesas-datatable"
          @edit-click="openDespesaModal"
        />

        <!-- Empty State -->
        <div v-else-if="!isLoadingPeriodDespesas" class="empty-state">
          <i class="pi pi-inbox"></i>
          <p>{{ t('Nenhuma despesa encontrada para o período selecionado') }}</p>
          <p class="empty-state-hint">{{ t('Adicione despesas usando os botões acima.') }}</p>
        </div>
      </div>

      <!-- DespesasCsvImportModal is centralized in ModalManager.vue - opened via eventBus -->
    </template>

    <!-- Step 1: Select Available Despesas (was Step 0) -->
    <template #step-1="{ stepConfig }">
      <div class="step-panel">
        <h3>{{ t('Selecione as despesas para vincular') }}</h3>
        <p class="step-description">
          {{ t('Selecione as despesas para vincular a este período de folha') }}
        </p>

        <!-- Loading State -->
        <div v-if="isLoadingDespesas" class="loading-state">
          <i class="pi pi-spin pi-spinner"></i>
          <p>{{ t('Carregando despesas...') }}</p>
        </div>

        <!-- Selection Summary -->
        <div v-else-if="displayDespesas.length > 0" class="selection-summary">
          <div class="summary-item">
            <span class="summary-label">{{ t('Disponíveis') }}:</span>
            <span class="summary-value">{{ displayDespesas.length }}</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-label">{{ t('Selecionados') }}:</span>
            <span class="summary-value">{{ selectedDespesas.length }}</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-label">{{ t('Total Selecionados') }}:</span>
            <span class="summary-value">{{ n(selectedTotal, 'currency') }}</span>
          </div>
        </div>

        <!-- Despesas DataTable with Selection -->
        <DataTable
          v-if="!isLoadingDespesas && displayDespesas.length > 0"
          v-model:selection="selectedDespesas"
          :config="despesasTableConfig"
          :data="displayDespesas"
          class="despesas-datatable"
          @edit-click="openDespesaModal"
        />

        <!-- Empty State -->
        <div v-else-if="!isLoadingDespesas" class="empty-state">
          <i class="pi pi-inbox"></i>
          <p>{{ t('Nenhuma despesa disponível para o período selecionado') }}</p>
          <p class="empty-state-hint">{{ t('Verifique o passo anterior para adicionar despesas.') }}</p>
        </div>
      </div>
    </template>

    <!-- Step 2: Complete Conciliation (was Step 1) -->
    <template #step-2="{ stepConfig }">
      <div class="step-panel">
        <h3>{{ t('Finalizar Conciliação de Despesas') }}</h3>
        <p class="step-description">
          {{ t('Revise o resumo final e complete o processo de conciliação de despesas.') }}
        </p>

        <!-- Final Summary -->
        <div class="final-summary">
          <div class="summary-section">
            <h4>{{ t('Resumo da Conciliação') }}</h4>
            <div class="summary-grid">
              <div class="summary-row">
                <span class="label">{{ t('Total de Despesas Selecionadas') }}:</span>
                <span class="value">{{ selectedDespesas.length }}</span>
              </div>
              <div class="summary-row highlight">
                <span class="label">{{ t('Total de Valor Selecionado') }}:</span>
                <span class="value">{{ n(selectedTotal, 'currency') }}</span>
              </div>
            </div>
          </div>

          <div class="completion-message">
            <i class="pi pi-check-circle"></i>
            <p>{{ t('Clique em "Finalizar Conciliação" para vincular as despesas selecionadas e finalizar o processo.') }}</p>
            <p class="note">{{ t('Após a conclusão, o valor conciliado será usado no cálculo de folha.') }}</p>
          </div>
        </div>

      </div>
    </template>
  </WizardModal>
  <!-- DespesasModal is centralized in ModalManager.vue and opened via eventBus -->
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '../../../ui/WizardModal.vue';
// DespesasCsvImportModal is centralized in ModalManager.vue - opened via eventBus
// DespesasModal is centralized in ModalManager.vue - opened via eventBus
import { useExpenseConciliation } from '../../../composables/useExpenseConciliation';
import { useDespesas } from '../../../composables/useDespesas';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

type Id = number | string;
type Frequency = 'weekly' | 'biweekly' | 'monthly';

interface DespesaRow {
  despesa_id: Id;
  despesa_valor?: number | string;
  despesa_titulo?: string;
  despesa_data_vencimento?: string | Date | null;
  despesa_data_lancamento?: string | Date | null;
  despesa_generated_from_payroll_id?: Id | null;
  categoria?: string;
  franquia?: string;
  status?: string;
  titulo?: string;
  valor?: number | string;
}

interface ConciliationSummary {
  status?: string;
  amount?: number;
  [key: string]: unknown;
}

interface StepChangeEvent {
  from: number;
  to: number;
}

interface Props {
  visible: boolean;
  periodId: number;
  franchiseId?: number;
  startDate?: string | Date;
  endDate?: string | Date;
  frequency?: Frequency;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'completed': [result: unknown];
  'closed': [];
}>();

// Composables
const { t, n } = useI18n();
const {
  getExpenseConciliationSummary,
  linkDespesasToPeriod,
  unlinkDespesaFromPeriod,
  completeExpenseConciliation: completeExpenseConciliationAPI,
  getLinkedDespesas: getLinkedDespesasAPI
} = useExpenseConciliation();

const {
  fetchAvailableDespesasForPayrollPeriod,
  fetchDespesasWithOverride
} = useDespesas();

// State
const isVisible = ref<boolean>(props.visible);
const currentStepIndex = ref<number>(0);
const availableDespesas = ref<DespesaRow[]>([]);
const selectedDespesas = ref<DespesaRow[]>([]);
const originallyLinkedDespesas = ref<DespesaRow[]>([]); // Track originally linked despesas for diff calculation
const conciliationStatus = ref<string>('pending');
const conciliationAmount = ref<number>(0);
const isCompleting = ref<boolean>(false);

// Step 0: Period Despesas state (NEW)
const periodDespesas = ref<DespesaRow[]>([]);
const isLoadingPeriodDespesas = ref<boolean>(false);
// csvImportModalRef removed - modal opened via eventBus

// Step 1: Loading state for despesas
const isLoadingDespesas = ref<boolean>(false);

// DespesasModal is opened via eventBus (centralized in ModalManager.vue)

// Wizard steps configuration for WizardModal (3 steps now)
const wizardStepsConfig = [
  {
    title: 'Verificar Despesas',
    description: 'Verifique as despesas do período e adicione novas se necessário',
    icon: 'pi-search-dollar',
    validate: () => true, // Optional step - user can proceed without changes
    optional: true
  },
  {
    title: 'Selecionar Despesas',
    description: 'Selecione as despesas para vincular a este período de folha',
    icon: 'pi-list',
    validate: () => selectedDespesas.value.length > 0
  },
  {
    title: 'Finalizar Conciliação',
    description: 'Finalize a conciliação de despesas',
    icon: 'pi-check-circle',
    validate: () => selectedDespesas.value.length > 0
  }
];

// Helper to format date for API
const formatDateForApi = (date: string | Date | null | undefined): string | null => {
  if (!date) return null;
  if (typeof date === 'string') return date;
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Helper to get despesa value
const getDespesaValor = (desp: DespesaRow): number => {
  // Despesas valor is already in currency units (not centavos)
  return parseFloat(String(desp.despesa_valor ?? 0)) || 0;
};

// Computed for Step 0: Period despesas total
const periodDespesasTotal = computed(() => {
  return periodDespesas.value.reduce((sum, desp) => {
    return sum + getDespesaValor(desp);
  }, 0);
});

// Computed
const selectedTotal = computed(() => {
  const total = selectedDespesas.value.reduce((sum, desp) => {
    return sum + getDespesaValor(desp);
  }, 0);

  // DEBUG: Log calculation details
  log('ExpenseConciliationWizard', 'selectedTotal', 'Calculating total', {
    selectedCount: selectedDespesas.value.length,
    total,
    firstDespesa: selectedDespesas.value[0]
  });

  return total;
});

// Merge available despesas with originally linked ones for display
// This ensures already-linked despesas appear in the list even if backend excludes them
const displayDespesas = computed(() => {
  const combined = [...availableDespesas.value];

  // Add any originally linked despesas that aren't in the available list
  for (const linked of originallyLinkedDespesas.value) {
    if (!combined.find(d => d.despesa_id === linked.despesa_id)) {
      combined.push(linked);
    }
  }

  return combined;
});

// Summary items for WizardModal
const summaryItems = computed(() => {
  return [
    {
      label: t('Total Selecionados'),
      value: selectedDespesas.value.length,
      icon: 'pi pi-check-square'
    },
    {
      label: t('Valor Selecionado'),
      value: n(selectedTotal.value, 'currency'),
      icon: 'pi pi-money-bill'
    }
  ];
});

// DataTable configuration for period despesas (Step 0 - view only, no selection)
const periodDespesasTableConfig = computed(() => ({
  columns: [
    {
      data: 'despesa_id',
      title: t('ID'),
      width: 70,
      sortable: false,
      renderType: 'clickable_id'
    },
    {
      data: 'franquia',
      title: t('Franquia')
    },
    {
      data: 'status',
      title: t('Status')
    },
    {
      data: 'categoria',
      title: t('Categoria')
    },
    {
      data: 'titulo',
      title: t('Título')
    },
    {
      data: 'valor',
      title: t('Valor'),
      renderType: 'currency',
      width: 120
    },
    {
      data: 'despesa_data_vencimento',
      title: t('Vencimento'),
      renderType: 'date',
      width: 100
    },
    {
      data: 'despesa_data_lancamento',
      title: t('Data Lançamento'),
      renderType: 'date',
      width: 100
    }
  ],
  checkbox: {
    enabled: false
  },
  pagination: false,
  showFilters: false,
  sortable: true,
  stripedRows: true,
  showGridlines: false,
  size: 'small',
  height: '350px',
  heightMode: 'fixed'
}));

// DataTable configuration for despesas (Step 1 - with selection)
const despesasTableConfig = computed(() => ({
  columns: [
    {
      data: 'despesa_id',
      title: t('ID'),
      width: 70,
      sortable: false,
      renderType: 'clickable_id'
    },
    {
      data: 'despesa_data_vencimento',
      title: t('Vencimento'),
      renderType: 'date',
      width: 100
    },
    {
      data: 'despesa_data_lancamento',
      title: t('Data Lançamento'),
      renderType: 'date',
      width: 100
    },
    {
      data: 'categoria',
      title: t('Categoria')
    },
    {
      data: 'despesa_titulo',
      title: t('Título')
    },
    {
      data: 'despesa_valor',
      title: t('Valor'),
      renderType: 'currency',
      width: 120
    }
  ],
  checkbox: {
    enabled: true,
    position: 'first',
    width: '3rem'
  },
  pagination: false,
  showFilters: false,
  sortable: true,
  stripedRows: true,
  showGridlines: false,
  size: 'small',
  height: '350px',
  heightMode: 'fixed'
}));

// Open DespesaModal when clicking on despesa ID (via eventBus)
const openDespesaModal = (despesaId: Id): void => {
  log('ExpenseConciliationWizard', 'openDespesaModal', 'Opening despesa modal via eventBus', { despesaId });
  eventBus.emit('modal.despesas.open', { id: despesaId });
};

// Step 0 handlers
const openNewDespesaModal = (): void => {
  log('ExpenseConciliationWizard', 'openNewDespesaModal', 'Opening new despesa modal');
  eventBus.emit('modal.despesas.open', {
    prefilledData: {
      despesa_franquia_id: props.franchiseId
    }
  });
};

const openCsvImportModal = (): void => {
  log('ExpenseConciliationWizard', 'openCsvImportModal', 'Opening CSV import modal via eventBus');
  eventBus.emit('modal.despesas-csv-import.open', {});
};

const handleCsvImported = (): void => {
  log('ExpenseConciliationWizard', 'handleCsvImported', 'CSV imported, reloading data');
  loadPeriodDespesas();
};

/**
 * Load period despesas for Step 0 (Overview).
 *
 * FRONTEND FILTERING STRATEGY:
 * ============================
 * This function uses the general carrega_despesas endpoint (via fetchDespesasWithOverride)
 * which returns ALL despesas for the period. We then apply frontend filtering to exclude
 * despesas generated from the CURRENT payroll period.
 *
 * WHY FRONTEND FILTER?
 * The backend getAvailableDespesasForPayrollPeriod (used in Step 2) handles cross-frequency
 * filtering (weekly→monthly OK, monthly→monthly blocked). But Step 0 needs a simpler rule:
 * just don't show despesas that THIS SPECIFIC PERIOD generated.
 *
 * CIRCULAR REFERENCE PREVENTION:
 * When a payroll period generates salary despesas, those despesas should NOT appear
 * in the same period's expense list. Example:
 * - Monthly period Dec 1-31 generates salary despesas on Dec 15
 * - Step 0 for this period should NOT show these despesas
 * - But a DIFFERENT monthly period or a weekly period CAN see them
 *
 * The field despesa_generated_from_payroll_id was added to dadosXcrud() in despesas_model.php
 * specifically to enable this frontend filtering.
 *
 * @see despesas_model.php dadosXcrud() - includes despesa_generated_from_payroll_id
 * @see getAvailableDespesasForPayrollPeriod() - backend frequency-based filtering for Step 2
 */
const loadPeriodDespesas = async (): Promise<void> => {
  log('ExpenseConciliationWizard', 'loadPeriodDespesas', 'Loading period despesas with queryOverride', {
    franchiseId: props.franchiseId,
    startDate: props.startDate,
    endDate: props.endDate,
    periodId: props.periodId
  });

  isLoadingPeriodDespesas.value = true;

  try {
    // Use queryOverride to fetch data for specific franchise and date range
    // This bypasses the global session filter to get despesas specifically for this period
    const queryOverride = {
      franquiaIds: [props.franchiseId] as Array<string | number>,
      dataInicio: formatDateForApi(props.startDate) ?? '',
      dataFim: formatDateForApi(props.endDate) ?? ''
    };

    const data = await fetchDespesasWithOverride({ queryOverride }) as DespesaRow[];

    // FRONTEND FILTER: Exclude despesas generated from the CURRENT payroll period
    // This prevents circular references where a period would show its own generated expenses.
    // The comparison uses Number() because despesa_generated_from_payroll_id may be a string.
    const filteredData = data.filter(d => {
      const generatedFromId = d.despesa_generated_from_payroll_id;
      // Keep despesas that: have no source period ID OR were generated by a DIFFERENT period
      return !generatedFromId || Number(generatedFromId) !== props.periodId;
    });

    periodDespesas.value = filteredData;

    log('ExpenseConciliationWizard', 'loadPeriodDespesas', 'Loaded data', {
      totalFetched: data.length,
      afterFilter: filteredData.length,
      total: periodDespesasTotal.value
    });
  } catch (error) {
    log('ExpenseConciliationWizard', 'loadPeriodDespesas', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar despesas do período') + ': ' + ((error as Error)?.message || String(error)));
    periodDespesas.value = [];
  } finally {
    isLoadingPeriodDespesas.value = false;
  }
};

// Handle step change - load data for the next step
const handleStepChange = async (event: StepChangeEvent): Promise<void> => {
  const { from, to } = event;
  log('ExpenseConciliationWizard', 'handleStepChange', 'Step changed', { from, to });
  currentStepIndex.value = to;

  // When entering Step 1, ALWAYS reload available and linked despesas
  // This ensures new despesas from Step 0 appear, and handles back>next navigation
  if (to === 1) {
    log('ExpenseConciliationWizard', 'handleStepChange', 'Entering Step 1 - reloading despesas');
    isLoadingDespesas.value = true;
    try {
      await Promise.all([
        loadAvailableDespesas(),
        loadLinkedDespesas()
      ]);
    } finally {
      isLoadingDespesas.value = false;
    }
  }
};

// Methods
const loadConciliationSummary = async (): Promise<void> => {
  log('ExpenseConciliationWizard', 'loadConciliationSummary', 'Loading summary', {
    periodId: props.periodId
  });

  try {
    const summary = await getExpenseConciliationSummary(props.periodId) as ConciliationSummary;
    conciliationStatus.value = summary.status ?? 'pending';
    conciliationAmount.value = summary.amount ?? 0;

    log('ExpenseConciliationWizard', 'loadConciliationSummary', 'Summary loaded', summary);
  } catch (error) {
    log('ExpenseConciliationWizard', 'loadConciliationSummary', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar resumo da conciliação'));
  }
};

/**
 * Load available despesas for Step 2 (Selection).
 *
 * BACKEND FILTERING STRATEGY (via getAvailableDespesasForPayrollPeriod):
 * ======================================================================
 * This uses the specialized backend endpoint that applies frequency-based filtering.
 *
 * CROSS-FREQUENCY EXPENSE FLOW:
 * - Weekly payroll generates salary despesas → these SHOULD appear in monthly periods
 * - Monthly payroll generates salary despesas → these should NOT appear in other monthly periods
 *
 * The backend query (despesas_model.php::getAvailableDespesasForPayrollPeriod) handles:
 * 1. Manual despesas: filtered by their dates, must not be linked to any period yet
 * 2. Payroll-generated despesas: filtered by SOURCE PERIOD's period_start_date,
 *    must be from a DIFFERENT frequency type
 *
 * WHY period_start_date for payroll-generated despesas?
 * A weekly period Oct 28 - Nov 3 generates expenses, but the period "belongs" to October
 * because it STARTS in October. Using period_start_date ensures these expenses appear
 * in October's monthly period, not November's.
 *
 * @see despesas_model.php getAvailableDespesasForPayrollPeriod() - backend filtering logic
 */
const loadAvailableDespesas = async (): Promise<void> => {
  log('ExpenseConciliationWizard', 'loadAvailableDespesas', 'Loading available despesas', { frequency: props.frequency });

  try {
    // Pass frequency to enable cross-frequency filtering on the backend
    // This allows weekly despesas to appear in monthly periods, but blocks
    // monthly despesas from appearing in other monthly periods
    if (!props.franchiseId || !props.startDate || !props.endDate) {
      log('ExpenseConciliationWizard', 'loadAvailableDespesas', 'Missing franchiseId/startDate/endDate, skipping fetch');
      availableDespesas.value = [];
      return;
    }
    const data = await fetchAvailableDespesasForPayrollPeriod(
      props.franchiseId,
      props.startDate,
      props.endDate,
      props.frequency
    ) as DespesaRow[];

    // DEBUG: Log first available despesa to check structure
    if (data.length > 0) {
      log('ExpenseConciliationWizard', 'loadAvailableDespesas', 'First available despesa structure', data[0]);
    }

    availableDespesas.value = data;

    log('ExpenseConciliationWizard', 'loadAvailableDespesas', 'Loaded', {
      count: data.length
    });
  } catch (error) {
    log('ExpenseConciliationWizard', 'loadAvailableDespesas', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar despesas disponíveis'));
    availableDespesas.value = [];
  }
};

const loadLinkedDespesas = async (): Promise<void> => {
  log('ExpenseConciliationWizard', 'loadLinkedDespesas', 'Loading linked despesas');

  try {
    const linkedDespesas = await getLinkedDespesasAPI(props.periodId) as DespesaRow[] | null | undefined;

    log('ExpenseConciliationWizard', 'loadLinkedDespesas', 'Raw API response', {
      count: linkedDespesas?.length,
      data: linkedDespesas
    });

    // Filter out invalid despesas (must have despesa_id)
    const validDespesas = (linkedDespesas || []).filter((d): d is DespesaRow => Boolean(d && d.despesa_id));

    log('ExpenseConciliationWizard', 'loadLinkedDespesas', 'After filtering', {
      rawCount: linkedDespesas?.length || 0,
      validCount: validDespesas.length
    });

    // Store original linked state for diff calculation (create separate copy)
    originallyLinkedDespesas.value = [...validDespesas];
    // Pre-select the linked despesas (create separate copy to avoid mutation)
    selectedDespesas.value = [...validDespesas];

    log('ExpenseConciliationWizard', 'loadLinkedDespesas', 'Loaded', {
      count: validDespesas.length
    });
  } catch (error) {
    log('ExpenseConciliationWizard', 'loadLinkedDespesas', 'Error', error);
    // Don't show error message - if there are no linked despesas, that's fine
    originallyLinkedDespesas.value = [];
    selectedDespesas.value = [];
  }
};


const completeExpenseConciliation = async (): Promise<void> => {
  if (selectedDespesas.value.length === 0) {
    eventBus.emit('globalMessage.warning', t('Por favor, selecione pelo menos uma despesa antes de finalizar'));
    return;
  }

  log('ExpenseConciliationWizard', 'completeExpenseConciliation', 'Starting conciliation process', {
    selectedCount: selectedDespesas.value.length,
    originalCount: originallyLinkedDespesas.value.length
  });

  isCompleting.value = true;

  try {
    // Calculate differences between original and selected
    const originalIds = originallyLinkedDespesas.value.map(d => d.despesa_id);
    const selectedIds = selectedDespesas.value.map(d => d.despesa_id);

    const toUnlink = originalIds.filter(id => !selectedIds.includes(id));
    const toLink = selectedIds.filter(id => !originalIds.includes(id));

    log('ExpenseConciliationWizard', 'completeExpenseConciliation', 'Diff calculated', {
      toUnlink: toUnlink.length,
      toLink: toLink.length
    });

    // First, unlink removed despesas (parallel for performance)
    if (toUnlink.length > 0) {
      log('ExpenseConciliationWizard', 'completeExpenseConciliation', `Unlinking ${toUnlink.length} despesas`);
      await Promise.all(
        toUnlink.map(despesaId => unlinkDespesaFromPeriod(props.periodId, despesaId))
      );
      log('ExpenseConciliationWizard', 'completeExpenseConciliation', 'Unlinks completed');
    }

    // Then, link new despesas
    if (toLink.length > 0) {
      log('ExpenseConciliationWizard', 'completeExpenseConciliation', `Linking ${toLink.length} despesas`);
      await linkDespesasToPeriod(props.periodId, toLink);
      log('ExpenseConciliationWizard', 'completeExpenseConciliation', 'Links completed');
    }

    // Finally, complete the conciliation
    const result = await completeExpenseConciliationAPI(props.periodId);

    log('ExpenseConciliationWizard', 'completeExpenseConciliation', 'Conciliation completed', result);

    // Emit completed event and close
    emit('completed', result);
    eventBus.emit('expense-conciliation.completed', { periodId: props.periodId, result });
    onClose();
  } catch (error) {
    log('ExpenseConciliationWizard', 'completeExpenseConciliation', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao finalizar conciliação de despesas'));
  } finally {
    isCompleting.value = false;
  }
};

const onClose = (): void => {
  log('ExpenseConciliationWizard', 'onClose', 'Closing wizard');
  emit('update:visible', false);
  emit('closed');
};

// Watch for visibility changes
watch(() => props.visible, async (newVal) => {
  isVisible.value = newVal;
  if (newVal) {
    // Reset wizard state when opening
    currentStepIndex.value = 0;
    originallyLinkedDespesas.value = []; // Reset original linked state
    periodDespesas.value = []; // Reset Step 0 data

    // Load Step 0 data (period despesas for viewing)
    loadPeriodDespesas();

    // Load initial data for Step 1 (in parallel)
    await Promise.all([
      loadConciliationSummary(),
      loadAvailableDespesas()
    ]);

    // Load already linked despesas (if any) to pre-select them
    await loadLinkedDespesas();
  }
});

// Event handler for despesa saved - refresh data
const handleDespesaSaved = async (): Promise<void> => {
  log('ExpenseConciliationWizard', 'handleDespesaSaved', 'Despesa saved, refreshing data', {
    currentStep: currentStepIndex.value
  });

  // Refresh Step 0 data (period despesas for viewing)
  if (currentStepIndex.value === 0) {
    await loadPeriodDespesas();
  }

  // Always refresh Step 1 data (available despesas)
  await loadAvailableDespesas();
};

// Lifecycle hooks for event listeners
onMounted(() => {
  log('ExpenseConciliationWizard', 'onMounted', 'Setting up event listeners');
  eventBus.on('despesa.saved', handleDespesaSaved);
  eventBus.on('despesa.csv.imported', handleCsvImported);
});

onBeforeUnmount(() => {
  log('ExpenseConciliationWizard', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('despesa.saved', handleDespesaSaved);
  eventBus.off('despesa.csv.imported', handleCsvImported);
});
</script>

<style scoped>
/* Step Content Panels */
.step-panel {
  width: 100%;
}

.step-panel h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

/* Selection Summary */
.selection-summary {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.summary-item {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}

.summary-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-color);
}

.summary-divider {
  width: 1px;
  height: 30px;
  background: var(--surface-border);
}

/* Despesas DataTable */
.despesas-datatable {
  margin-top: 1rem;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-color-secondary);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

.empty-state-hint {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  margin-top: 0.5rem;
  font-style: italic;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-color-secondary);
}

.loading-state i {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.loading-state p {
  margin: 0;
  font-size: 1rem;
}

/* Final Summary */
.final-summary {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem 0;
}

.summary-section h4 {
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.summary-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--surface-border);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row.highlight {
  background: var(--primary-50);
  padding: 1rem;
  margin: 0 -1rem;
  border-radius: 6px;
  border: 1px solid var(--primary-200);
}

.summary-row .label {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.summary-row .value {
  font-weight: 600;
  font-size: 1.125rem;
  color: var(--text-color);
}

.summary-row.highlight .value {
  font-size: 1.5rem;
  color: var(--primary-color);
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  text-transform: uppercase;
  font-weight: 600;
}

.status-badge.pending {
  background: var(--yellow-100);
  color: var(--yellow-900);
}

.status-badge.in_progress {
  background: var(--blue-100);
  color: var(--blue-900);
}

.status-badge.completed {
  background: var(--green-100);
  color: var(--green-900);
}

/* Completion Message */
.completion-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  background: var(--green-50);
  border: 2px solid var(--green-200);
  border-radius: 8px;
  text-align: center;
}

.completion-message i {
  font-size: 3rem;
  color: var(--green-500);
}

.completion-message p {
  margin: 0;
  color: var(--text-color);
  font-size: 1rem;
  line-height: 1.5;
}

.completion-message .note {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  font-style: italic;
}

/* Responsive */
@media (max-width: 768px) {
  .selection-summary {
    flex-direction: column;
    gap: 0.75rem;
  }

  .summary-divider {
    width: 100%;
    height: 1px;
  }
}
</style>
